import React from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import AppProvider from 'providers/appProvider';
import { router } from 'routes';
import 'helpers/initFA';
import './culture/i18n';
import { setDynamicFavicon } from './components/common/favicon';
import {
  APP_VERSION_KEY,
  UPDATE_REQUIRED_KEY,
} from 'helpers/appUpdate';
import { initDomainConfig } from './config/domainConfig';
import { applyBranding } from './config/brandConfig';

applyBranding();

const initializeFavicon = () => {
  const customerId =
    sessionStorage.getItem('customerId') ||
    localStorage.getItem('customerId');

  if (customerId) {
    setDynamicFavicon(customerId);
  } else {
    // Fallback: Delay check until customerId is available
    const interval = setInterval(() => {
      const storedCustomerId =
        sessionStorage.getItem('customerId') ||
        localStorage.getItem('customerId');

      if (storedCustomerId) {
        setDynamicFavicon(storedCustomerId);
        clearInterval(interval);
      }
    }, 50);
  }
};

initializeFavicon();

// Add cache-busting query string to CSS and JS files
const cacheBuster = `?v=${new Date().getTime()}`;

if (
  process.env.NODE_ENV === 'PRODUCTION' ||
  process.env.NODE_ENV === 'DEVELOPMENT'
) {
  const cssLinks = document.querySelectorAll('link[rel="stylesheet"]');
  cssLinks.forEach(link => {
    const href = link.getAttribute('href');
    if (href) {
      link.setAttribute('href', href + cacheBuster);
    }
  });

  const scripts = document.querySelectorAll('script[src]');
  scripts.forEach(script => {
    const src = script.getAttribute('src');
    if (src) {
      script.setAttribute('src', src + cacheBuster);
    }
  });
}

function checkForUpdate() {
  fetch('/version.json', { cache: 'no-store' })
    .then(res => res.json())
    .then(data => {
      const storedVersion = localStorage.getItem(APP_VERSION_KEY);
      const isLoggedIn =
        sessionStorage.getItem('isLoggedIn') === 'true' ||
        localStorage.getItem('isLoggedIn') === 'true';

      // Truly first-time visitor
      if (!storedVersion) {
        localStorage.setItem(APP_VERSION_KEY, data.version);
        return;
      }

      // Same version → nothing to do
      if (storedVersion === data.version) {
        return;
      }

      // Version changed
      if (isLoggedIn) {
        // Stamp the new version now so a reload (whether the session turns out
        // valid or invalid) doesn't re-trigger this same branch in a loop.
        localStorage.setItem(APP_VERSION_KEY, data.version);
        // Let AppUpdateModal show the toast, validate the session, then refresh.
        sessionStorage.setItem(UPDATE_REQUIRED_KEY, 'true');
      } else {
        // Not logged in — safe to just pick up the new version silently.
        localStorage.setItem(APP_VERSION_KEY, data.version);
        window.location.reload();
      }
    })
    .catch(() => { });
}

checkForUpdate();
setInterval(checkForUpdate, 30 * 1000);

const bootstrap = async () => {
  await initDomainConfig();

const container = document.getElementById('main');
const root = createRoot(container);

root.render(
  // <React.StrictMode>
  <AppProvider>
    <RouterProvider router={router} />
  </AppProvider>
  // </React.StrictMode>
);
};

bootstrap();